import { DELAI_SAISIE_DIFFEREE_MS } from '@rh/shared';
import { expireLeBrut, lireBrouillon, lireEnvoi } from './format';
import { ID_ONGLET, nouvelIdentifiant } from './identifiants';
import {
  ConnexionPerdue,
  ouvrirBaseIdb,
  type BaseSaisies,
  type EnregistrementBrut,
  type NomMagasin,
  type TransactionMagasins,
} from './idb';
import { baseMemoire, type InstantaneSaisies } from './stockage-memoire';
import {
  FORMAT_SAISIES,
  NOM_BASE,
  type Brouillon,
  type CleSaisie,
  type DemandeMiseEnFile,
  type EnvoiContact,
  type FinContact,
  type Recensement,
  type RefusEnvoi,
  type ResultatMiseEnFile,
  type ResumeEnvoi,
} from './types';
import { notationVide } from './validation';

/**
 * File des saisies du panneau de contact sur le téléphone (docs/UI.md § Écran 3).
 * Chaque changement d'état est une transaction : mise en file et fusion, puis
 * finalisations **conditionnelles** — une confirmation, une fin ou un refus ne
 * s'appliquent qu'à la révision ou à l'`envoiId` réellement envoyés. Le moteur
 * ne réécrit jamais une copie gardée en mémoire : une fusion faite pendant un
 * envoi n'est donc jamais perdue. Le stockage ne contrôle que la forme ; les
 * règles métier sont vérifiées avant (`validation.ts`) puis par l'API.
 */

export interface GardeRefus {
  etape: 'enregistrement';
  revision: number;
}
export type GardeRefusFin = { etape: 'fin'; envoiId: string };

export interface StockageFile {
  readonly genre: 'indexeddb' | 'memoire';
  mettreEnFile(demande: DemandeMiseEnFile, maintenant: number): Promise<ResultatMiseEnFile>;
  listerAEnvoyer(utilisateurId: string, maintenant: number): Promise<CleSaisie[]>;
  lire(cle: CleSaisie): Promise<EnvoiContact | undefined>;
  noterTentative(cle: CleSaisie, maintenant: number): Promise<void>;
  confirmerEnregistrement(
    cle: CleSaisie,
    revisionEnvoyee: number,
  ): Promise<'termine' | 'reste_fin' | 'fusion_en_attente' | 'absent'>;
  terminer(cle: CleSaisie, envoiIdFin: string): Promise<void>;
  planifierReprise(cle: CleSaisie, prochaineTentativeLe: number, panne: { le: number; message: string }): Promise<void>;
  marquerRefus(cle: CleSaisie, refus: RefusEnvoi, garde: GardeRefus | GardeRefusFin): Promise<boolean>;
  retirerEnregistrement(cle: CleSaisie): Promise<void>;
  abandonner(cle: CleSaisie): Promise<void>;
  reprendreMaintenant(utilisateurId: string): Promise<void>;
  purgerExpires(maintenant: number): Promise<number>;
  purgerUtilisateur(utilisateurId: string): Promise<number>;
  purgerAutresQue(utilisateurId: string): Promise<number>;
  recenser(maintenant: number): Promise<Recensement>;
  resumes(utilisateurId: string): Promise<ResumeEnvoi[]>;
  prochaineEcheance(utilisateurId: string): Promise<number | null>;
  lireBrouillon(cle: CleSaisie): Promise<Brouillon | undefined>;
  ecrireBrouillon(brouillon: Brouillon): Promise<void>;
  supprimerBrouillon(cle: CleSaisie): Promise<void>;
  fermer(): void;
}

export interface OptionsStockage {
  fabrique?: IDBFactory;
  nom?: string;
  surVersionChange?: () => void;
  surBloque?: () => void;
  /** Le stockage vient de passer en mémoire (quota dépassé, autre version) : bandeau. */
  surBascule?: () => void;
}

export const MESSAGE_FIN_DEJA_EN_FILE = 'Ce contact est déjà clôturé sur ce téléphone : l’envoi est en attente.';
export const MESSAGE_STOCKAGE_INDISPONIBLE =
  'Enregistrement impossible sur ce téléphone : la saisie n’a pas été gardée. Réessayez.';
export const MESSAGE_SAISIE_PLUS_RECENTE =
  'Une saisie faite avec une version plus récente de l’application attend sur ce contact : rechargez la page.';
const MESSAGE_SAISIE_MAL_FORMEE = 'Saisie mal formée : elle n’a pas été gardée sur ce téléphone.';
const VINGT_QUATRE_HEURES_MS = 24 * 60 * 60 * 1000;

const cleDe = (utilisateurId: string, contactId: string): CleSaisie => [utilisateurId, contactId];
const cleTexte = (cle: IDBValidKey | CleSaisie) => JSON.stringify(cle);
/** Compte de la clé brute, lisible même quand l'élément ne l'est pas (la clé vient du chemin de clé). */
const compteDeCle = (cle: IDBValidKey): string | null =>
  Array.isArray(cle) && typeof cle[0] === 'string' ? cle[0] : null;

function estQuotaDepasse(erreur: unknown): boolean {
  return erreur instanceof DOMException && erreur.name === 'QuotaExceededError';
}

const heureFin = (fin: FinContact | null): string | null =>
  fin === null ? null : fin.genre === 'cloture' ? fin.termineLe : fin.evalueeLe;

/** Rang suivant : au-delà de la séquence et de tout rang présent, même illisible (index unique). */
async function prochainOrdre(tx: TransactionMagasins): Promise<number> {
  const sequence = await tx.lireMeta('sequence');
  let max = typeof sequence === 'number' && Number.isFinite(sequence) ? sequence : 0;
  for (const { valeur } of await tx.tous('envois')) {
    const ordre = (valeur as { ordre?: unknown } | null)?.ordre;
    if (typeof ordre === 'number' && Number.isFinite(ordre) && ordre > max) max = ordre;
  }
  const ordre = Math.floor(max) + 1;
  await tx.ecrireMeta('sequence', ordre);
  return ordre;
}

async function envoisLisibles(tx: TransactionMagasins): Promise<EnvoiContact[]> {
  const lignes = await tx.tous('envois');
  return lignes.map((ligne) => lireEnvoi(ligne.valeur)).filter((e): e is EnvoiContact => e !== null);
}

async function copierBase(base: BaseSaisies): Promise<InstantaneSaisies> {
  return base.executer('readonly', async (tx) => ({
    envois: await tx.tous('envois'),
    brouillons: await tx.tous('brouillons'),
    meta: { sequence: await tx.lireMeta('sequence') },
  }));
}

function resumeEnvoi(e: EnvoiContact): ResumeEnvoi {
  const saisie = e.saisie;
  const fin = e.fin;
  return {
    utilisateurId: e.utilisateurId,
    contactId: e.contactId,
    candidatureId: e.candidatureId,
    type: e.type,
    debuteLe: e.debuteLe,
    etat: e.etat,
    etapes: { enregistrement: e.enregistrement !== null, fin: fin?.genre ?? null },
    modifieLe: e.modifieLe,
    heureFin: heureFin(fin),
    potentiel: saisie?.type === 'telephonique' ? saisie.potentiel : (e.enregistrement?.corps.potentiel ?? null),
    noteAffichee: fin?.genre === 'evaluation' ? fin.noteAffichee : null,
    baremeVersion:
      fin?.genre === 'evaluation'
        ? fin.bareme.version
        : saisie?.type === 'presentiel'
          ? (saisie.bareme?.version ?? null)
          : null,
    refus: e.refus
      ? {
          code: e.refus.code,
          message: e.refus.message,
          contenuEfface: e.refus.contenuEfface,
          action: e.refus.action,
        }
      : null,
    tentatives: e.tentatives,
    prochaineTentativeLe: e.prochaineTentativeLe,
    dernierePanne: e.dernierePanne?.message ?? null,
    expireLe: e.expireLe,
  };
}

function resumeBrouillon(b: Brouillon): ResumeEnvoi {
  return {
    utilisateurId: b.utilisateurId,
    contactId: b.contactId,
    candidatureId: b.candidatureId,
    type: b.type,
    debuteLe: b.debuteLe,
    etat: 'brouillon',
    etapes: { enregistrement: false, fin: null },
    modifieLe: b.modifieLe,
    heureFin: null,
    potentiel: b.saisie.type === 'telephonique' ? b.saisie.potentiel : null,
    noteAffichee: null,
    baremeVersion: b.saisie.type === 'presentiel' ? (b.saisie.bareme?.version ?? null) : null,
    refus: null,
    tentatives: 0,
    prochaineTentativeLe: 0,
    dernierePanne: null,
    expireLe: b.expireLe,
  };
}

class StockageSaisies implements StockageFile {
  private base: BaseSaisies;
  /** Passage en mémoire en cours : les opérations suivantes l'attendent. */
  private bascule: Promise<void> | null = null;
  private reouverture: Promise<BaseSaisies> | null = null;
  private readonly enVol = new Set<Promise<unknown>>();
  private persistanceDemandee = false;
  private ferme = false;

  constructor(
    base: BaseSaisies,
    private readonly options: OptionsStockage = {},
    private readonly rouvrir: (() => Promise<BaseSaisies>) | null = null,
  ) {
    this.base = base;
  }

  get genre(): 'indexeddb' | 'memoire' {
    return this.base.genre;
  }

  // -------------------------------------------------------------------------
  // Exécution : reprise de connexion, quota, autre version
  // -------------------------------------------------------------------------

  /**
   * Toute opération passe ici. Hors bascule, elle est inscrite en vol dès
   * l'appel (sans attente) : les écritures lancées par `surVersionChange`
   * partent donc encore sur le disque avant la fermeture.
   */
  private executer<T>(mode: 'readonly' | 'readwrite', travail: (tx: TransactionMagasins) => Promise<T>): Promise<T> {
    // Derrière une bascule, l'opération partira en mémoire ; elle n'est pas « en vol »
    // (la bascule, qui attend les opérations en vol, l'attendrait sans fin).
    if (this.bascule) return this.bascule.then(() => this.executerSurBase(mode, travail));
    const operation = this.executerSurBase(mode, travail);
    this.enVol.add(operation);
    const retirer = () => this.enVol.delete(operation);
    operation.then(retirer, retirer);
    return operation;
  }

  private async executerSurBase<T>(
    mode: 'readonly' | 'readwrite',
    travail: (tx: TransactionMagasins) => Promise<T>,
  ): Promise<T> {
    if (this.ferme) throw new Error('Stockage des saisies fermé');
    const base = this.base;
    try {
      return await base.executer(mode, travail);
    } catch (erreur) {
      if (erreur instanceof ConnexionPerdue && this.rouvrir) {
        // Safari coupe parfois la connexion d'une application restée en arrière-plan : une réouverture.
        await this.rouvrirUneFois(base);
        return this.base.executer(mode, travail);
      }
      if (mode === 'readwrite' && base.genre === 'indexeddb' && estQuotaDepasse(erreur)) {
        await this.basculerEnMemoire(base);
        return this.base.executer(mode, travail);
      }
      throw erreur;
    }
  }

  private async rouvrirUneFois(perdue: BaseSaisies): Promise<void> {
    if (this.base !== perdue) return;
    this.reouverture ??= this.rouvrir!().finally(() => {
      this.reouverture = null;
    });
    const nouvelle = await this.reouverture;
    if (this.base === perdue) this.base = nouvelle;
  }

  /** Quota dépassé : on emporte ce qui est lisible sur le disque et on continue en mémoire. */
  private async basculerEnMemoire(ancienne: BaseSaisies): Promise<void> {
    if (this.base !== ancienne) return;
    const instantane = await copierBase(ancienne).catch(
      (): InstantaneSaisies => ({ envois: [], brouillons: [], meta: {} }),
    );
    if (this.base !== ancienne) return;
    this.base = baseMemoire(instantane);
    ancienne.fermer();
    this.options.surBascule?.();
  }

  /**
   * Une autre version de l'application demande la base. L'appelant vide ses
   * écritures en attente dans son rappel ; on laisse partir tout ce qui est en
   * vol, on copie la base, on la ferme (l'autre onglet peut monter de
   * version) et cet onglet continue en mémoire jusqu'au rechargement.
   */
  surVersionChange(): void {
    try {
      this.options.surVersionChange?.();
    } catch (erreur) {
      console.error(erreur);
    }
    if (this.bascule || this.base.genre !== 'indexeddb') return;
    const ancienne = this.base;
    this.bascule = (async () => {
      await new Promise((resoudre) => setTimeout(resoudre, 0));
      await Promise.allSettled([...this.enVol]);
      const instantane = await copierBase(ancienne).catch(
        (): InstantaneSaisies => ({ envois: [], brouillons: [], meta: {} }),
      );
      ancienne.fermer();
      this.base = baseMemoire(instantane);
      this.options.surBascule?.();
    })().finally(() => {
      this.bascule = null;
    });
  }

  private demanderPersistance(): void {
    if (this.persistanceDemandee || this.base.genre !== 'indexeddb') return;
    this.persistanceDemandee = true;
    try {
      void globalThis.navigator?.storage?.persist?.().catch(() => undefined);
    } catch {
      /* navigateur sans StorageManager */
    }
  }

  // -------------------------------------------------------------------------
  // Mise en file et fusion
  // -------------------------------------------------------------------------

  async mettreEnFile(demande: DemandeMiseEnFile, maintenant: number): Promise<ResultatMiseEnFile> {
    const cle = cleDe(demande.utilisateurId, demande.contact.id);
    let resultat: ResultatMiseEnFile;
    try {
      resultat = await this.executer('readwrite', async (tx): Promise<ResultatMiseEnFile> => {
        const brutExistant = await tx.lire('envois', cle);
        const existant = brutExistant === undefined ? undefined : lireEnvoi(brutExistant);
        if (existant === null) return { ok: false, raison: 'STOCKAGE_INDISPONIBLE', message: MESSAGE_SAISIE_PLUS_RECENTE };
        // Double appui : la fin déjà en file part d'abord, on ne la remplace pas.
        if (existant?.etat === 'en_attente' && existant.fin) {
          return { ok: false, raison: 'FIN_DEJA_EN_FILE', message: MESSAGE_FIN_DEJA_EN_FILE };
        }
        const brutBrouillon = await tx.lire('brouillons', cle);
        const brouillon = brutBrouillon === undefined ? undefined : lireBrouillon(brutBrouillon);

        let envoi: EnvoiContact;
        if (existant?.etat === 'en_attente') {
          // Fusion : même élément, même rang, même échéance ; la révision non partie est remplacée.
          envoi = { ...existant };
        } else {
          // Nouvel élément, ou reprise d'un refus : nouveau rang, enregistrement refusé gardé
          // (le corps de la demande le remplace). Une clôture ou une évaluation ne repart que
          // sur Clôturer ou Valider, jamais derrière un simple Enregistrer.
          const creeLe = existant?.creeLe ?? brouillon?.creeLe ?? maintenant;
          envoi = {
            format: FORMAT_SAISIES,
            utilisateurId: demande.utilisateurId,
            contactId: demande.contact.id,
            candidatureId: demande.contact.candidatureId,
            type: demande.contact.type,
            debuteLe: demande.contact.debuteLe,
            ordre: await prochainOrdre(tx),
            creeLe,
            expireLe: creeLe + DELAI_SAISIE_DIFFEREE_MS,
            modifieLe: maintenant,
            derniereRevision: existant?.derniereRevision ?? 0,
            enregistrement: existant?.enregistrement ?? null,
            fin: null,
            saisie: null,
            etat: 'en_attente',
            refus: null,
            tentatives: 0,
            prochaineTentativeLe: 0,
            dernierePanne: null,
          };
        }
        if (demande.corps !== null) {
          envoi.derniereRevision += 1;
          envoi.enregistrement = { corps: demande.corps, revision: envoi.derniereRevision };
        }
        if (demande.genre === 'cloture') {
          envoi.fin = { genre: 'cloture', envoiId: nouvelIdentifiant(), termineLe: demande.termineLe };
        } else if (demande.genre === 'evaluation') {
          envoi.fin = {
            genre: 'evaluation',
            envoiId: nouvelIdentifiant(),
            evalueeLe: demande.evalueeLe,
            entree: demande.entree,
            bareme: demande.bareme,
            noteAffichee: demande.noteAffichee,
          };
        }
        envoi.saisie = demande.saisie;
        envoi.etat = 'en_attente';
        envoi.refus = null;
        envoi.tentatives = 0;
        envoi.prochaineTentativeLe = 0;
        envoi.modifieLe = maintenant;
        if (!lireEnvoi(envoi)) return { ok: false, raison: 'STOCKAGE_INDISPONIBLE', message: MESSAGE_SAISIE_MAL_FORMEE };

        await tx.ecrire('envois', envoi);
        if (envoi.fin) {
          // Clôture ou évaluation en file : le formulaire ne reviendra pas, son brouillon non plus.
          if (brouillon) await tx.effacer('brouillons', cle);
        } else if (brouillon) {
          await tx.ecrire('brouillons', { ...brouillon, revisionMiseEnFile: demande.brouillonRevision });
        } else if (
          existant?.etat === 'refuse' &&
          existant.fin?.genre === 'evaluation' &&
          demande.saisie.type === 'presentiel' &&
          !notationVide(demande.saisie)
        ) {
          // L'évaluation refusée ne repart pas derrière la trame : sa notation reste sur le
          // téléphone, en brouillon, jusqu'à la prochaine validation.
          const revision = demande.brouillonRevision ?? 0;
          const conserve: Brouillon = {
            format: FORMAT_SAISIES,
            utilisateurId: demande.utilisateurId,
            contactId: demande.contact.id,
            candidatureId: demande.contact.candidatureId,
            type: demande.contact.type,
            debuteLe: demande.contact.debuteLe,
            saisie: demande.saisie,
            revision,
            revisionMiseEnFile: revision,
            onglet: ID_ONGLET,
            creeLe: envoi.creeLe,
            modifieLe: maintenant,
            expireLe: envoi.expireLe,
          };
          await tx.ecrire('brouillons', conserve);
        }
        return { ok: true, envoi };
      });
    } catch (erreur) {
      console.error(erreur);
      return { ok: false, raison: 'STOCKAGE_INDISPONIBLE', message: MESSAGE_STOCKAGE_INDISPONIBLE };
    }
    if (resultat.ok) this.demanderPersistance();
    return resultat;
  }

  listerAEnvoyer(utilisateurId: string, maintenant: number): Promise<CleSaisie[]> {
    return this.executer('readonly', async (tx) =>
      (await envoisLisibles(tx))
        .filter(
          (e) =>
            e.utilisateurId === utilisateurId &&
            e.etat === 'en_attente' &&
            e.prochaineTentativeLe <= maintenant &&
            (e.enregistrement !== null || e.fin !== null),
        )
        .sort((a, b) => a.ordre - b.ordre)
        .map((e) => cleDe(e.utilisateurId, e.contactId)),
    );
  }

  lire(cle: CleSaisie): Promise<EnvoiContact | undefined> {
    return this.executer('readonly', async (tx) => lireEnvoi(await tx.lire('envois', cle)) ?? undefined);
  }

  /** Modifie un élément lisible en attente ; rien sinon. */
  private modifierEnAttente(cle: CleSaisie, maj: (e: EnvoiContact) => Partial<EnvoiContact>): Promise<void> {
    return this.executer('readwrite', async (tx) => {
      const e = lireEnvoi(await tx.lire('envois', cle));
      if (e?.etat === 'en_attente') await tx.ecrire('envois', { ...e, ...maj(e) });
    });
  }

  noterTentative(cle: CleSaisie, _maintenant: number): Promise<void> {
    return this.modifierEnAttente(cle, (e) => ({ tentatives: e.tentatives + 1 }));
  }

  planifierReprise(cle: CleSaisie, prochaineTentativeLe: number, panne: { le: number; message: string }): Promise<void> {
    return this.modifierEnAttente(cle, () => ({ prochaineTentativeLe, dernierePanne: panne }));
  }

  // -------------------------------------------------------------------------
  // Finalisations conditionnelles
  // -------------------------------------------------------------------------

  confirmerEnregistrement(
    cle: CleSaisie,
    revisionEnvoyee: number,
  ): Promise<'termine' | 'reste_fin' | 'fusion_en_attente' | 'absent'> {
    return this.executer('readwrite', async (tx) => {
      const e = lireEnvoi(await tx.lire('envois', cle));
      if (!e || e.etat !== 'en_attente') return 'absent';
      // Une fusion est arrivée pendant l'envoi : elle reste, et partira à son tour.
      if (e.enregistrement && e.enregistrement.revision !== revisionEnvoyee) return 'fusion_en_attente';
      if (e.fin) {
        await tx.ecrire('envois', { ...e, enregistrement: null, tentatives: 0, dernierePanne: null });
        return 'reste_fin';
      }
      await tx.effacer('envois', cle);
      // Le brouillon n'est plus utile s'il n'a pas bougé depuis la mise en file, sauf une
      // notation d'entretien pas encore validée (le PUT n'emporte que la trame).
      const b = lireBrouillon(await tx.lire('brouillons', cle));
      if (b && b.revisionMiseEnFile === b.revision && (b.saisie.type === 'telephonique' || notationVide(b.saisie))) {
        await tx.effacer('brouillons', cle);
      }
      return 'termine';
    });
  }

  terminer(cle: CleSaisie, envoiIdFin: string): Promise<void> {
    return this.executer('readwrite', async (tx) => {
      const e = lireEnvoi(await tx.lire('envois', cle));
      if (!e || e.fin?.envoiId !== envoiIdFin) return;
      await tx.effacer('envois', cle);
      if (lireBrouillon(await tx.lire('brouillons', cle))) await tx.effacer('brouillons', cle);
    });
  }

  marquerRefus(cle: CleSaisie, refus: RefusEnvoi, garde: GardeRefus | GardeRefusFin): Promise<boolean> {
    return this.executer('readwrite', async (tx) => {
      const e = lireEnvoi(await tx.lire('envois', cle));
      if (!e || e.etat !== 'en_attente') return false;
      if (refus.contenuEfface) {
        // Fiche anonymisée ou supprimée : toute révision est condamnée, le contenu part
        // du téléphone quelle que soit la garde. Seule la trace du refus reste.
        await tx.ecrire('envois', {
          ...e,
          etat: 'refuse',
          refus,
          saisie: null,
          enregistrement: null,
          fin: null,
          prochaineTentativeLe: 0,
        });
        if ((await tx.lire('brouillons', cle)) !== undefined) await tx.effacer('brouillons', cle);
        return true;
      }
      const tenue =
        garde.etape === 'enregistrement'
          ? e.enregistrement?.revision === garde.revision
          : e.fin?.envoiId === garde.envoiId;
      if (!tenue) return false;
      await tx.ecrire('envois', { ...e, etat: 'refuse', refus, prochaineTentativeLe: 0 });
      return true;
    });
  }

  /** « Envoyer seulement l'évaluation » : le PUT est abandonné, la fin repart seule. */
  retirerEnregistrement(cle: CleSaisie): Promise<void> {
    return this.executer('readwrite', async (tx) => {
      const e = lireEnvoi(await tx.lire('envois', cle));
      if (!e) return;
      if (!e.fin) {
        await tx.effacer('envois', cle);
        return;
      }
      await tx.ecrire('envois', {
        ...e,
        ordre: e.etat === 'refuse' ? await prochainOrdre(tx) : e.ordre,
        enregistrement: null,
        etat: 'en_attente',
        refus: null,
        tentatives: 0,
        prochaineTentativeLe: 0,
        dernierePanne: null,
      });
    });
  }

  abandonner(cle: CleSaisie): Promise<void> {
    return this.executer('readwrite', async (tx) => {
      await tx.effacer('envois', cle);
      await tx.effacer('brouillons', cle);
    });
  }

  reprendreMaintenant(utilisateurId: string): Promise<void> {
    return this.executer('readwrite', async (tx) => {
      for (const e of await envoisLisibles(tx)) {
        if (e.utilisateurId === utilisateurId && e.etat === 'en_attente' && e.prochaineTentativeLe > 0) {
          await tx.ecrire('envois', { ...e, prochaineTentativeLe: 0 });
        }
      }
    });
  }

  // -------------------------------------------------------------------------
  // Purges
  // -------------------------------------------------------------------------

  /** Efface, dans les deux magasins, les lignes retenues ; rend le nombre de saisies (clés) touchées. */
  private purger(retenir: (ligne: EnregistrementBrut) => boolean): Promise<number> {
    return this.executer('readwrite', async (tx) => {
      const effacees = new Set<string>();
      for (const nom of ['envois', 'brouillons'] satisfies NomMagasin[]) {
        for (const ligne of await tx.tous(nom)) {
          if (!retenir(ligne)) continue;
          await tx.effacer(nom, ligne.cle);
          effacees.add(cleTexte(ligne.cle));
        }
      }
      return effacees.size;
    });
  }

  /** 7 jours : lisible ou non, seul compte un `expireLe` numérique ; sans lui, l'élément corrompu part. */
  purgerExpires(maintenant: number): Promise<number> {
    return this.purger(({ valeur }) => {
      const echeance = expireLeBrut(valeur);
      return echeance === null || echeance <= maintenant;
    });
  }

  purgerUtilisateur(utilisateurId: string): Promise<number> {
    return this.purger(({ cle }) => compteDeCle(cle) === utilisateurId);
  }

  purgerAutresQue(utilisateurId: string): Promise<number> {
    return this.purger(({ cle }) => compteDeCle(cle) !== utilisateurId);
  }

  // -------------------------------------------------------------------------
  // Lectures pour l'interface
  // -------------------------------------------------------------------------

  /**
   * Une saisie = une clé (envoi, brouillon ou les deux). Une trace de refus à
   * contenu effacé n'est plus une saisie : elle n'est pas comptée.
   */
  recenser(maintenant: number): Promise<Recensement> {
    return this.executer('readonly', async (tx) => {
      const saisies = new Map<string, { utilisateurId: string; expireLe: number }>();
      const illisibles = new Set<string>();
      const retenir = (cle: string, utilisateurId: string, expireLe: number) => {
        const connue = saisies.get(cle);
        saisies.set(cle, { utilisateurId, expireLe: Math.min(expireLe, connue?.expireLe ?? expireLe) });
      };
      for (const ligne of await tx.tous('envois')) {
        const e = lireEnvoi(ligne.valeur);
        if (!e) illisibles.add(cleTexte(ligne.cle));
        else if (!e.refus?.contenuEfface) retenir(cleTexte(ligne.cle), e.utilisateurId, e.expireLe);
      }
      for (const ligne of await tx.tous('brouillons')) {
        const b = lireBrouillon(ligne.valeur);
        if (!b) illisibles.add(cleTexte(ligne.cle));
        else retenir(cleTexte(ligne.cle), b.utilisateurId, b.expireLe);
      }
      const parUtilisateur: Record<string, number> = {};
      let plusProcheExpiration: number | null = null;
      let expirentSous24h = 0;
      for (const { utilisateurId, expireLe } of saisies.values()) {
        parUtilisateur[utilisateurId] = (parUtilisateur[utilisateurId] ?? 0) + 1;
        plusProcheExpiration = Math.min(expireLe, plusProcheExpiration ?? expireLe);
        if (expireLe - maintenant < VINGT_QUATRE_HEURES_MS) expirentSous24h += 1;
      }
      return {
        total: saisies.size,
        parUtilisateur,
        illisibles: illisibles.size,
        plusProcheExpiration,
        expirentSous24h,
      };
    });
  }

  resumes(utilisateurId: string): Promise<ResumeEnvoi[]> {
    return this.executer('readonly', async (tx) => {
      const envois = (await envoisLisibles(tx))
        .filter((e) => e.utilisateurId === utilisateurId)
        .sort((a, b) => a.ordre - b.ordre);
      const avecEnvoi = new Set(envois.map((e) => e.contactId));
      const brouillons = (await tx.tous('brouillons'))
        .map((ligne) => lireBrouillon(ligne.valeur))
        .filter((b): b is Brouillon => b !== null && b.utilisateurId === utilisateurId && !avecEnvoi.has(b.contactId))
        .sort((a, b) => a.modifieLe - b.modifieLe);
      return [...envois.map(resumeEnvoi), ...brouillons.map(resumeBrouillon)];
    });
  }

  prochaineEcheance(utilisateurId: string): Promise<number | null> {
    return this.executer('readonly', async (tx) => {
      let prochaine: number | null = null;
      for (const e of await envoisLisibles(tx)) {
        if (e.utilisateurId !== utilisateurId || e.etat !== 'en_attente') continue;
        if (e.enregistrement === null && e.fin === null) continue;
        prochaine = Math.min(e.prochaineTentativeLe, prochaine ?? e.prochaineTentativeLe);
      }
      return prochaine;
    });
  }

  // -------------------------------------------------------------------------
  // Brouillons
  // -------------------------------------------------------------------------

  lireBrouillon(cle: CleSaisie): Promise<Brouillon | undefined> {
    return this.executer('readonly', async (tx) => lireBrouillon(await tx.lire('brouillons', cle)) ?? undefined);
  }

  /**
   * L'échéance n'est jamais prolongée : un brouillon garde la date de la
   * première écriture locale sur ce contact, envoi en file compris.
   */
  ecrireBrouillon(brouillon: Brouillon): Promise<void> {
    return this.executer('readwrite', async (tx) => {
      const cle = cleDe(brouillon.utilisateurId, brouillon.contactId);
      const brut = await tx.lire('brouillons', cle);
      const existant = brut === undefined ? undefined : lireBrouillon(brut);
      if (existant === null) throw new Error(MESSAGE_SAISIE_PLUS_RECENTE);
      const envoi = lireEnvoi(await tx.lire('envois', cle));
      // Fiche anonymisée ou supprimée : une écriture partie avant le refus ne ramène pas son contenu.
      if (envoi?.etat === 'refuse' && envoi.refus?.contenuEfface) return;
      const creeLe = Math.min(brouillon.creeLe, existant?.creeLe ?? Infinity, envoi?.creeLe ?? Infinity);
      const complet: Brouillon = {
        ...brouillon,
        format: FORMAT_SAISIES,
        creeLe,
        expireLe: creeLe + DELAI_SAISIE_DIFFEREE_MS,
      };
      if (!lireBrouillon(complet)) throw new TypeError(MESSAGE_SAISIE_MAL_FORMEE);
      await tx.ecrire('brouillons', complet);
    });
  }

  supprimerBrouillon(cle: CleSaisie): Promise<void> {
    return this.executer('readwrite', async (tx) => {
      await tx.effacer('brouillons', cle);
    });
  }

  fermer(): void {
    this.ferme = true;
    this.base.fermer();
  }
}

/** Stockage sur une base donnée (tests, repli en mémoire). */
export function creerStockage(base: BaseSaisies, options: OptionsStockage = {}): StockageFile {
  return new StockageSaisies(base, options);
}

/** IndexedDB, ou StockageMemoire (genre 'memoire') si l'ouverture échoue. */
export async function ouvrirStockage(options: OptionsStockage = {}): Promise<StockageFile> {
  const fabrique = options.fabrique ?? (globalThis as { indexedDB?: IDBFactory }).indexedDB;
  if (!fabrique) return new StockageSaisies(baseMemoire(), options);
  let stockage: StockageSaisies | null = null;
  let premiere: BaseSaisies | null = null;
  const ouvrir = () =>
    ouvrirBaseIdb({
      fabrique,
      nom: options.nom ?? NOM_BASE,
      // Avant que le stockage n'existe, rien n'est en vol : on ferme aussitôt.
      surVersionChange: () => (stockage ? stockage.surVersionChange() : premiere?.fermer()),
      surBloque: options.surBloque,
    });
  try {
    premiere = await ouvrir();
  } catch (erreur) {
    console.warn('Base locale indisponible, saisies gardées en mémoire', erreur);
    return new StockageSaisies(baseMemoire(), options);
  }
  stockage = new StockageSaisies(premiere, options, ouvrir);
  return stockage;
}
